import { useCallback, useEffect, useState } from "react";
import { Message, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "semantic-ui-react";
import { useRestClientContext } from "../../../contexts/RestClientContext";
import Property from "../../../rest/Property";
import {
	SapImprovementNiceNames,
	sapImprovementUnitsMap,
	SmartTarget,
	SmartTargetFields,
	SmartTargetParams,
	SmartTargetStatus,
	SmartTargetWithPivot,
} from "../../../types/types";
import SmartTargetResultModal from "../../Modal/SmartTargetResultModal";
import Preloader from "../../UI/Preloader/Preloader";
import GenerateSmartTarget from "./GenerateSmartTarget";
import SmartTargetStatusChip from "./SmartTargetStatusChip";
import SmartTargetTableDropdown from "./SmartTargetTableDropdown";

import "./SmartTargetsPanel.scss";
import { CustomImprovementsModalPanelProps } from "../CustomImprovementSetsModal";

interface SmartTargetsPanelProps extends CustomImprovementsModalPanelProps {
	property: Property;
}

const formatResultValue = (target: SmartTargetFields, value: number) => {
	let formattedValue;
	if (sapImprovementUnitsMap[target].unit === "£") {
		formattedValue = `£${value}`;
	} else if (!!sapImprovementUnitsMap[target].unit) {
		formattedValue = `${value} ${sapImprovementUnitsMap[target].unit}`;
	} else {
		formattedValue = "" + value;
	}

	if (sapImprovementUnitsMap[target].timeFrame) {
		formattedValue += ` /${sapImprovementUnitsMap[target].timeFrame}`;
	}
	return formattedValue;
};

export default function SmartTargetsPanel({ teamId, property }: SmartTargetsPanelProps) {
	const [smartTargets, setSmartTargets] = useState<SmartTargetWithPivot[]>([]);
	const [selectedSmartTarget, setSelectedSmartTarget] = useState<SmartTargetWithPivot | null>(null);
	const [isResultsModalOpen, setIsResultsModalOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	/** Used to decrease polling times when left open */
	const [timesPolled, setTimesPolled] = useState<number>(0);
	const [errors, setErrors] = useState<string[]>([]);

	const client = useRestClientContext();
	const currentValues = property.fetchCurrentSapValues();

	const updateSmartTargetsList = useCallback(() => {
		client
			.fetchAllPropertySmartTargets(teamId, property.id, ["improvements"])
			.then((response: Response) => response.json())
			.then((json: any) => {
				setSmartTargets(json);
				setLoading(false);
			});
		setTimesPolled(timesPolled + 1);
		// we don't wanna create a loop here with timesPolled calling this again
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, property.id, teamId]);

	useEffect(() => {
		setLoading(true);
		updateSmartTargetsList();
	}, [property, updateSmartTargetsList]);

	useEffect(() => {
		const interval = setInterval(
			updateSmartTargetsList,
			// clamp between 1 min and 10 minutes depending on iterations
			Math.min(Math.max(1, Math.sqrt(timesPolled)), 10) * 60 * 1000
		);
		return () => clearTimeout(interval);
	}, [timesPolled, updateSmartTargetsList]);

	/**
	 * Get the full smart target data for the selected target - this is then stored in state so that we don't need to
	 * fetch again if the modal is reopened.
	 * @param smartTarget The smart target to refresh the data for.
	 * @return Promise<SmartTarget>
	 */
	const updateSmartTargetList = (smartTarget: SmartTarget | SmartTargetWithPivot): Promise<SmartTargetWithPivot> => {
		return new Promise<SmartTargetWithPivot>((resolve, reject) => {
			client
				.fetchPropertySmartTargets(teamId, property.id, smartTarget.id)
				.then((response: Response) => response.json())
				.then((json: SmartTargetWithPivot) => {
					setSelectedSmartTarget(json);
					resolve(json);

					const newSmartTargets = smartTargets.map((iterSmartTarget) => {
						if (iterSmartTarget.id === smartTarget.id) {
							return json;
						}
						return iterSmartTarget;
					});

					setSmartTargets(newSmartTargets);
				})
				.catch(() => reject("Could not update smart target data"));
		});
	};

	const formatResultsList = (smartTarget: SmartTargetWithPivot) => {
		if (smartTarget.status === SmartTargetStatus.RUNNING || smartTarget.status === SmartTargetStatus.PENDING) {
			return <span className="result-light">In Progress...</span>;
		}
		if (smartTarget.status === SmartTargetStatus.ERROR || !smartTarget.customImprovementSet) {
			return <span className="result-light">No Results Available</span>;
		}

		if ("custom_improvements" in smartTarget.customImprovementSet) {
			// trim name down if it is too long
			const namesList = smartTarget.customImprovementSet.custom_improvements.map(
				(improvement) => improvement.improvement.name
			);
			if (namesList.length > 2) {
				const remainder = namesList.length - 2;
				return (
					<>
						<b>{namesList.slice(0, 2).join(", ")}</b>
						<span className="result-light">
							+ {remainder} {remainder === 1 ? "Other" : "Others"}
						</span>
					</>
				);
			}
		}
		return smartTarget.customImprovementSet.name;
	};

	const onViewSmartTarget = (smartTarget: SmartTargetWithPivot) => {
		setSelectedSmartTarget(smartTarget);
		setIsResultsModalOpen(true);
	};

	const cancelPendingSmartTarget = (smartTarget: SmartTargetWithPivot) => {
		client.cancelPendingSmartTargets(teamId, property, smartTarget).then(() => {
			const newSmartTargets = smartTargets.map((targetItem: SmartTargetWithPivot) =>
				targetItem.id === smartTarget.id ? { ...targetItem, status: SmartTargetStatus.CANCELLED } : targetItem
			);
			setSmartTargets(newSmartTargets);
		});
	};

	const onDelete = (smartTarget: SmartTargetWithPivot) => {
		client.deleteSmartTargets(teamId, property, smartTarget).then(() => {
			const newSmartTargets = smartTargets.filter(
				(targetItem: SmartTargetWithPivot) => targetItem.id !== smartTarget.id
			);
			setSmartTargets(newSmartTargets);
		});
	};

	const onSubmit = (params: SmartTargetParams) => {
		setErrors([]);
		setLoading(true);
		client
			.createSmartTargets(teamId, property, params)
			.then(() => updateSmartTargetsList()) // NB: Trigger reload
			.catch((err) => {
				setLoading(false);
				const jsonError = err.message.match(/{.*}/);
				if (jsonError) {
					const parsed = JSON.parse(jsonError[0]);
					setErrors(Object.values(parsed));
				} else {
					setErrors(["Error submitting smart target. Check inputs and try again."]);
				}
			});
	};

	return (
		<>
			{loading ? (
				<Preloader />
			) : !!smartTargets.length ? (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHeaderCell>ID</TableHeaderCell>
							<TableHeaderCell>Target</TableHeaderCell>
							<TableHeaderCell>Current Value</TableHeaderCell>
							<TableHeaderCell>Target Value</TableHeaderCell>
							<TableHeaderCell>Status</TableHeaderCell>
							<TableHeaderCell>Results</TableHeaderCell>
							<TableHeaderCell />
						</TableRow>
					</TableHeader>
					<TableBody>
						{smartTargets.map((smartTarget: SmartTargetWithPivot) => (
							<TableRow
								key={smartTarget.id}
								className="smart-target-item-row"
								onClick={() => onViewSmartTarget(smartTarget)}
							>
								<TableCell>{smartTarget.id}</TableCell>
								<TableCell>{SapImprovementNiceNames[smartTarget.target]}</TableCell>
								<TableCell>
									{!!currentValues[smartTarget.target].current ? (
										formatResultValue(
											smartTarget.target,
											currentValues[smartTarget.target].current!
										)
									) : (
										<span className="result-light">N/A</span>
									)}
								</TableCell>
								<TableCell>{formatResultValue(smartTarget.target, smartTarget.value)}</TableCell>
								<TableCell>
									<SmartTargetStatusChip type={smartTarget.status} />
								</TableCell>
								<TableCell>{formatResultsList(smartTarget)}</TableCell>
								<TableCell className="actions-dropdown">
									<SmartTargetTableDropdown
										smartTargetStatus={smartTarget.status}
										onCancel={() => cancelPendingSmartTarget(smartTarget)}
										onDelete={() => onDelete(smartTarget)}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			) : (
				<Message info>
					<Message.Header>No previous smart targets found</Message.Header>
					<Message.Content>Create a smart target by filling out the form below</Message.Content>
				</Message>
			)}
			<GenerateSmartTarget onSubmit={onSubmit} isLoading={loading} currentValues={currentValues} />
			{errors && errors.map((error) => <Message error>{error}</Message>)}
			{isResultsModalOpen && selectedSmartTarget && (
				<SmartTargetResultModal
					smartTarget={selectedSmartTarget}
					property={property}
					updateSmartTarget={updateSmartTargetList}
					onClose={() => setIsResultsModalOpen(false)}
				/>
			)}
		</>
	);
}
